import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
  HttpResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { TabSdk } from '@techextensor/tab-sdk';
import { TokenRefreshService } from '../services/token-refresh.service';
import { API, getUserFriendlyErrorMessage, StorageConstants } from '@techextensor/tab-core-utility';
import { environment } from '../../../environments/environment';

declare const Zone: any;

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly tokenRefreshService = inject(TokenRefreshService);

  /**
   * HTTP Interceptor for token management and error handling
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    req = this.injectScreenHeaders(req);

    // Handle refresh active state
    if (this.tokenRefreshService.isRefreshActive()) {
      return this.handleRefreshActiveState(req, next);
    }

    // Normal request flow
    return next.handle(req).pipe(
      tap(event => this.handleSuccessResponse(event)),
      catchError(error => this.handleErrorResponse(req, next, error))
    );
  }

  /**
   * Reads Zone context set by TabFormRendererService and adds:
   *   - Screen: <screenId> — when inside a screen zone
   * Does NOT override a header already explicitly set on the request.
   */
  private injectScreenHeaders(req: HttpRequest<any>): HttpRequest<any> {
    const screenId = Zone.current.get(StorageConstants.Screen);
    if (!screenId) return req;

    let headers = req.headers;
    if (!headers.has(StorageConstants.Screen)) {
      headers = headers.set(StorageConstants.Screen, screenId);
    }
    
    return req.clone({ headers });
  }

  /**
   * Handle requests during token refresh
   */
  private handleRefreshActiveState(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Allow refresh API calls to proceed with static token
    if (req.url.includes(API.refreshToken)) {
      return next.handle(req.clone({
        setHeaders: { [StorageConstants.Authorization]: environment.commonConfig.staticToken }
      }));
    }

    // Queue all other requests
    return this.tokenRefreshService.queueRequest(req, next);
  }

  /**
   * Handle successful responses - check for server-side errors
   */
  private handleSuccessResponse(event: HttpEvent<any>): void {
    if (!(event instanceof HttpResponse)) return;

    const { body } = event;
    if (body?.Errors?.length > 0) {
      this.showUserFriendlyError(body.Errors);
    }
  }

  /**
   * Handle HTTP error responses
   */
  private handleErrorResponse(
    req: HttpRequest<any>, next: HttpHandler, error: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    // Skip auth handling if explicitly requested
    if (req.headers.get('X-Skip-Auth')) {
      return throwError(error);
    }

    // Handle 401 Unauthorized
    if (error.status === 401) {
      return this.handleUnauthorizedError(req, next, error);
    }

    // Handle other errors
    this.showUserFriendlyError(error?.error?.Errors);
    return throwError(error);
  }

  /**
   * Handle 401 errors with token refresh
   */
  private handleUnauthorizedError(
    req: HttpRequest<any>, next: HttpHandler, error: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    return this.tokenRefreshService.handleTokenRefresh(req, next, error)
      .pipe(
        catchError(() => {
          const path = window.location.pathname.match(/^\/([^\/]+)/)?.[1];
          if (this.authService._authenticated || path !== 'sign-in') {
            this.authService.signOut();
          }
          return throwError(error);
        })
      );
  }

  /**
   * Display user-friendly error messages
   */
  private showUserFriendlyError(errors: any): void {
    errors?.forEach((errorMessage: string, index: number) => {
      console.error(errorMessage);
      if(index === 0) {
        const userFriendlyMessage = getUserFriendlyErrorMessage(errorMessage);
        if (userFriendlyMessage) {
          TabSdk.ui.showError({ message: userFriendlyMessage });
        } else {
          TabSdk.ui.showError({ message: errorMessage });
        }
      }
      // if (environment.name === 'Development')
    });
  }
}